import { useState } from "react";
import useData from "./useData";
import usePage from "./usePage";

const useAdjustment = () => {
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get all adjustments for a pay period
  const getByPayPeriodId = async (payPeriodId) => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "Adjustment",
        action: "getByPayPeriodId",
        data: { payPeriodId },
      });

      if (results?.isSuccessful && Array.isArray(results.data)) {
        setAdjustments(results.data);
        return results.data;
      }

      showMessage(results?.message || "Failed to load adjustments", "error");
      return [];
    } catch (err) {
      console.error("[useAdjustment.getByPayPeriodId] Error:", err);
      showMessage(err.message, "error");
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Get single adjustment by ID
  const getById = async (adjustmentId) => {
    try {
      const results = await executeAction({
        entityName: "Adjustment",
        action: "getById",
        data: { id: adjustmentId },
      });

      if (results?.isSuccessful) {
        return results.data;
      } else {
        showMessage(results?.message || "Failed to load adjustment", "error");
        return null;
      }
    } catch (err) {
      console.error("[useAdjustment.getById] Error:", err);
      showMessage(err.message, "error");
      return null;
    }
  };

  // Add new adjustment
  const add = async (adjustmentData) => {
    try {
      const results = await executeAction({
        entityName: "Adjustment",
        action: "add",
        data: adjustmentData,
      });

      if (results?.isSuccessful) {
        showMessage("Adjustment created successfully", "success");
        return results.data;
      } else {
        showMessage(results?.message || "Failed to create adjustment", "error");
        return null;
      }
    } catch (err) {
      console.error("[useAdjustment.add] Error:", err);
      showMessage(err.message, "error");
      return null;
    }
  };

  // Update adjustment
  const update = async (adjustmentData) => {
    try {
      const results = await executeAction({
        entityName: "Adjustment",
        action: "update",
        data: adjustmentData,
      });

      if (results?.isSuccessful) {
        showMessage("Adjustment updated successfully", "success");
        return results.data;
      } else {
        showMessage(results?.message || "Failed to update adjustment", "error");
        return null;
      }
    } catch (err) {
      console.error("[useAdjustment.update] Error:", err);
      showMessage(err.message, "error");
      return null;
    }
  };

  // Delete adjustment
  const deleteAdjustment = async (adjustmentId) => {
    try {
      const results = await executeAction({
        entityName: "Adjustment",
        action: "delete",
        data: { id: adjustmentId },
      });

      if (results?.isSuccessful) {
        showMessage("Adjustment deleted successfully", "success");
        return true;
      } else {
        showMessage(results?.message || "Failed to delete adjustment", "error");
        return false;
      }
    } catch (err) {
      console.error("[useAdjustment.deleteAdjustment] Error:", err);
      showMessage(err.message, "error");
      return false;
    }
  };

  // Approve adjustment
  const approve = async (adjustmentId) => {
    try {
      const results = await executeAction({
        entityName: "Adjustment",
        action: "approve",
        data: { id: adjustmentId },
      });

      if (results?.isSuccessful) {
        showMessage("Adjustment approved", "success");
        return results.data;
      } else {
        showMessage(
          results?.message || "Failed to approve adjustment",
          "error",
        );
        return null;
      }
    } catch (err) {
      console.error("[useAdjustment.approve] Error:", err);
      showMessage(err.message, "error");
      return null;
    }
  };

  return {
    adjustments,
    loading,
    getByPayPeriodId,
    getById,
    add,
    update,
    deleteAdjustment,
    approve,
  };
};

export default useAdjustment;
